import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { apiClient } from '@/lib/apiClient';
import type { User } from '@/_types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const schema = z
  .object({
    lead_credits: z.coerce.number().int('Must be a whole number').min(0, 'Cannot be negative'),
    email_credits: z.coerce.number().int('Must be a whole number').min(0, 'Cannot be negative'),
    amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  })
  .refine((d) => d.lead_credits > 0 || d.email_credits > 0, {
    message: 'At least one credit type must be greater than 0',
    path: ['lead_credits'],
  });

type FormValues = z.infer<typeof schema>;

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

async function sendPaymentLink(
  userId: string,
  data: FormValues,
): Promise<void> {
  await apiClient.post(`/users/${userId}/billing/payment-link`, data);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  user: User | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// CreditPaymentModal
// ---------------------------------------------------------------------------

export default function CreditPaymentModal({ user, onClose }: Props) {
  const [succeeded, setSucceeded] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { lead_credits: 0, email_credits: 0, amount: 0 },
  });

  const handleClose = () => {
    reset();
    setSucceeded(false);
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!user) return;
    try {
      await sendPaymentLink(user.id, values);
      setSucceeded(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send payment link');
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Credit Payment Link</DialogTitle>
          <DialogDescription>
            Generate a Stripe payment link and email it to{' '}
            <span className="font-medium text-foreground">{user?.name}</span> (
            {user?.email}).
          </DialogDescription>
        </DialogHeader>

        {succeeded ? (
          <div className="py-4">
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              Payment link sent successfully to <strong>{user?.email}</strong>.
              The credits will be added to their organization after payment is
              completed.
            </p>
            <DialogFooter className="mt-4">
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="lead_credits">Lead Credits</Label>
              <Input
                id="lead_credits"
                type="number"
                min={0}
                placeholder="0"
                {...register('lead_credits')}
              />
              {errors.lead_credits && (
                <p className="text-xs text-destructive">{errors.lead_credits.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email_credits">Email Credits</Label>
              <Input
                id="email_credits"
                type="number"
                min={0}
                placeholder="0"
                {...register('email_credits')}
              />
              {errors.email_credits && (
                <p className="text-xs text-destructive">{errors.email_credits.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="amount">Amount (USD)</Label>
              <Input
                id="amount"
                type="number"
                min={0.01}
                step={0.01}
                placeholder="0.00"
                {...register('amount')}
              />
              {errors.amount && (
                <p className="text-xs text-destructive">{errors.amount.message}</p>
              )}
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending…' : 'Send Payment Link'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
